import { useEffect, useMemo, useState } from "react";
import Select from 'react-select';
import moment from "moment";
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import StatsDateSelector, { getMinAllowedDate } from "./StatsDateSelector";
import StatsComparativaPosicions from "./StatsComparativaPosicions";
import { fetchAPI, postAPI, getSubdomain } from "../../utils/utils";
import { downloadImage } from "../../utils/upload-image";
import { PLACEHOLDER_PROFILE_NO_PHOTO } from "../../utils/placeholders";
import "./StatsPositionsTab.css";
import { getCastellerFullName, getCastellerNameWithMote } from "../../utils/castellerName";

const COLLA = getSubdomain();

const DAYS = ['Dl', 'Dt', 'Dc', 'Dj', 'Dv', 'Ds', 'Dg'];
const MONTHS = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];

// Group events by week (ISO week number) and find which days have events
const groupByWeek = (eventItems) => {
    const weeks = {};
    const daysWithEvents = new Set();
    
    eventItems.forEach(({ event, count }) => {
        const date = moment(event['data-esperada-inici']);
        const weekKey = date.format('GGGG-WW'); // ISO year + week
        const dayOfWeek = (date.isoWeekday() - 1); // 0 = Monday
        
        daysWithEvents.add(dayOfWeek);
        
        if (!weeks[weekKey]) {
            weeks[weekKey] = {
                weekStart: date.clone().startOf('isoWeek'),
                days: Array(7).fill(null)
            };
        }
        
        // If there's already an event on this day, combine counts
        if (weeks[weekKey].days[dayOfWeek]) {
            weeks[weekKey].days[dayOfWeek].count += count;
            weeks[weekKey].days[dayOfWeek].events.push(event);
        } else {
            weeks[weekKey].days[dayOfWeek] = { count, events: [event] };
        }
    });
    
    // Sort days that have events
    const activeDays = [...daysWithEvents].sort((a, b) => a - b);
    
    const weeksList = Object.entries(weeks)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, data]) => data);
    
    return { weeks: weeksList, activeDays };
};

const PART_COLORS = {
    pinya: '#3b82f6',
    folre: '#f59e0b',
    manilles: '#10b981',
    puntals: '#8b5cf6',
    tronc: '#64748b'
};

const getBundleParts = (bundle) => {
    if (!bundle?.parts) return [];
    return Object.keys(bundle.parts)
        .map(part => part.toLowerCase())
        .filter(part => ['pinya', 'folre', 'manilles', 'puntals', 'tronc'].includes(part));
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const TAB_DEFINITIONS = [
    { key: 'pinya', label: 'Pinya', color: '#3b82f6' },      // blue
    { key: 'folre', label: 'Folre', color: '#f59e0b' },      // amber (contrasts with blue)
    { key: 'manilles', label: 'Manilles', color: '#10b981' }, // emerald (contrasts with amber)
    { key: 'puntals', label: 'Puntals', color: '#ef4444' },   // red (contrasts with green)
    { key: 'tronc', label: 'Tronc', color: '#8b5cf6' },       // violet (contrasts with red)
    { key: 'organitzacio', label: 'Organització', color: '#ec4899' }, // pink (contrasts with violet)
];

const normalizeText = value => value
    ? value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    : '';

const normalizeTabKey = tab => {
    const normalized = normalizeText(tab);
    const match = TAB_DEFINITIONS.find(def => normalizeText(def.key) === normalized || normalizeText(def.label) === normalized);
    return match ? match.key : null;
};

function StatsPositionsTab({ events, castellersInfo }) {
    // Read initial values from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const minAllowedDate = getMinAllowedDate();
    const defaultStartDate = minAllowedDate;
    
    // Ensure URL param start date respects minAllowedDate
    const urlStartDate = urlParams.get('start');
    const initialStartDate = urlStartDate && moment(urlStartDate).isSameOrAfter(minAllowedDate) 
        ? urlStartDate 
        : defaultStartDate;
    
    const [start_date, setStart_date] = useState(initialStartDate);
    const [end_date, setEnd_date] = useState(urlParams.get('end') || moment().format('YYYY-MM-DD'));
    const [selectedUser, setSelectedUser] = useState(urlParams.get('casteller') || null);
    const [includeAssaigs, setIncludeAssaigs] = useState(true);
    const [includeActuacions, setIncludeActuacions] = useState(true);
    const [loading, setLoading] = useState(false);
    const [positionsByEvent, setPositionsByEvent] = useState([]);
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [allEtiquetes, setAllEtiquetes] = useState([]);
    const [userEtiquetes, setUserEtiquetes] = useState([]);
    const [loadingEtiquetes, setLoadingEtiquetes] = useState(true);
    const [loadingUserEtiquetes, setLoadingUserEtiquetes] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [hiddenEventIds, setHiddenEventIds] = useState(new Set());
    const [userProfilePic, setUserProfilePic] = useState(null);
    const [bundles, setBundles] = useState([]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const newSelectedUser = params.get('casteller') || null;
            const newStartDate = params.get('start');
            const newEndDate = params.get('end');

            setSelectedUser(newSelectedUser);
            
            if (newStartDate && moment(newStartDate).isSameOrAfter(minAllowedDate)) {
                setStart_date(newStartDate);
            } else {
                setStart_date(defaultStartDate);
            }
            
            if (newEndDate) {
                setEnd_date(newEndDate);
            } else {
                setEnd_date(moment().format('YYYY-MM-DD'));
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [minAllowedDate, defaultStartDate]);

    // Fetch all etiquetes on mount
    useEffect(() => {
        setLoadingEtiquetes(true);
        fetchAPI('/etiquetes', (data) => {
            setAllEtiquetes(data);
            setLoadingEtiquetes(false);
        });
    }, []);

    // Fetch bundles on mount
    useEffect(() => {
        fetchAPI('/bundles', setBundles);
    }, []);

    const bundlesById = useMemo(() => {
        return bundles.reduce((acc, bundle) => {
            acc[bundle.id] = bundle;
            return acc;
        }, {});
    }, [bundles]);

    // Fetch user etiquetes when user is selected
    useEffect(() => {
        if (selectedUser) {
            setLoadingUserEtiquetes(true);
            fetchAPI('/etiquetes/' + selectedUser, (data) => {
                setUserEtiquetes(data);
                setLoadingUserEtiquetes(false);
            });
        } else {
            setUserEtiquetes([]);
            setLoadingUserEtiquetes(false);
        }
    }, [selectedUser]);

    // Load profile picture when user is selected
    useEffect(() => {
        if (selectedUser) {
            setUserProfilePic(null); // Reset to loading state
            downloadImage(selectedUser, COLLA)
                .then(base64 => setUserProfilePic(base64 || false))
                .catch(() => setUserProfilePic(false));
        } else {
            setUserProfilePic(null);
        }
    }, [selectedUser]);

    // Update URL when filters change
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        
        if (selectedUser) {
            params.set('casteller', selectedUser);
        } else {
            params.delete('casteller');
        }
        
        if (start_date !== defaultStartDate) {
            params.set('start', start_date);
        } else {
            params.delete('start');
        }
        
        if (end_date !== moment().format('YYYY-MM-DD')) {
            params.set('end', end_date);
        } else {
            params.delete('end');
        }

        const newUrl = params.toString() 
            ? `${window.location.pathname}?${params.toString()}`
            : window.location.pathname;
        
        // Only update URL if it actually changed
        if (newUrl !== window.location.pathname + window.location.search) {
            window.history.pushState({}, '', newUrl);
        }
    }, [selectedUser, start_date, end_date]);

    const filteredEvents = useMemo(() => {
        return events
            .filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]'))
            .filter(e => (includeAssaigs ? true : e.tipus !== 'assaig'))
            .filter(e => (includeActuacions ? true : e.tipus !== 'actuació'))
            .filter(e => ['assaig', 'actuació'].includes(e.tipus))
            .filter(e => !(e.tipus === 'actuació' && e.title?.toLowerCase().includes('comercial')))
            .sort((a, b) => moment(a['data-esperada-inici']).diff(moment(b['data-esperada-inici'])));
    }, [events, start_date, end_date, includeAssaigs, includeActuacions]);

    const allUsers = useMemo(() => {
        return Object.keys(castellersInfo || {})
            .map(user => ({
                value: user,
                label: getCastellerNameWithMote(castellersInfo[user])
            }));
    }, [castellersInfo]);

    // Helper functions that work with inline box metadata (from batch endpoint)
    const formatCordo = (cordo) => {
        if (!cordo && cordo !== 0) return null;
        const cordoNum = parseInt(cordo, 10);
        if (isNaN(cordoNum)) return null;
        if (cordoNum === 0) return null; // nucli - don't show cordó number
        if (cordoNum > 3) return '+4';
        if (cordoNum === 1) return '1r';
        if (cordoNum === 2) return '2n';
        if (cordoNum === 3) return '3r';
        return `${cordoNum}`;
    };

    // Format floor number (pis) to Catalan name
    const formatPis = (pis) => {
        if (pis === 1) return 'baix';
        if (pis === 2) return 'segon';
        if (pis === 3) return 'terç';
        if (pis === 4) return 'quart';
        if (pis === 5) return 'quint';
        if (pis === 6) return 'sisè';
        if (pis === 7) return 'setè';
        if (pis === 8) return 'vuitè';
        if (pis === 9) return 'novè';
        return `pis ${pis}`;
    };

    // Format pom type to display name
    const formatPom = (pom) => {
        const pomLower = pom?.toLowerCase();
        if (pomLower === 'dos') return 'dosos';
        if (pomLower === 'acotxador') return 'acotxador';
        if (pomLower === 'enxaneta') return 'enxaneta';
        return pom;
    };

    // Get Catalan preposition "de" or "d'" based on word
    const getPreposition = (word) => {
        const vowels = ['a', 'e', 'i', 'o', 'u', 'à', 'è', 'é', 'í', 'ò', 'ó', 'ú'];
        const firstChar = word?.toLowerCase()?.charAt(0);
        return vowels.includes(firstChar) ? "d'" : 'de ';
    };

    // Gets label info from box metadata directly (no bundleCache lookup needed)
    const getBoxLabelInfoFromBox = (box) => {
        const pestanya = box?.pestanya;
        const perfil = box?.perfil;
        const cordo = box?.cordo;
        const pom = box?.pom;
        const pis = box?.pis;

        const pestanyaLabel = pestanya ? normalizeText(pestanya) : null;
        const perfilLabel = perfil && typeof perfil === 'string' && perfil.trim().length > 0 ? perfil.trim().toLowerCase() : null;
        const cordoLabel = formatCordo(cordo);
        const isPinya = pestanyaLabel === 'pinya';
        const isTronc = pestanyaLabel === 'tronc';

        // Special handling for tronc positions: use pom or pis instead of perfil
        if (isTronc) {
            if (pom) {
                // Pom positions (enxaneta, acotxador, dosos)
                return { label: formatPom(pom), hasPerfil: true };
            }
            if (pis !== null && pis !== undefined) {
                // Floor positions (segon, terç, quart...)
                return { label: formatPis(pis), hasPerfil: true };
            }
            // Fallback for tronc without pis/pom
            return { label: 'tronc', hasPerfil: false };
        }

        // Only include cordó when pestanya is "pinya"
        // Priority: pestanya + perfil + cordo (pinya only) > pestanya + perfil > pestanya + cordo (pinya only) > pestanya
        if (pestanyaLabel && perfilLabel && cordoLabel && isPinya) {
            return { label: `${cordoLabel} ${perfilLabel} de la ${pestanyaLabel}`, hasPerfil: true };
        }
        if (pestanyaLabel && perfilLabel) {
            const preposition = isPinya ? 'de la' : 'del';
            return { label: `${perfilLabel} ${preposition} ${pestanyaLabel}`, hasPerfil: true };
        }
        if (pestanyaLabel && cordoLabel && isPinya) {
            return { label: `${cordoLabel} cordó de la ${pestanyaLabel}`, hasPerfil: false };
        }
        if (pestanyaLabel) {
            return { label: pestanyaLabel, hasPerfil: false };
        }

        // Fallback: never show caixaId, use pestanya or generic label
        return { label: 'altres', hasPerfil: false };
    };

    const getBoxTabFromBox = (box) => {
        return normalizeTabKey(box?.pestanya);
    };

    const toggleGroup = (pestanya) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [pestanya]: !prev[pestanya]
        }));
    };

    const addEtiqueta = (etiquetaId) => {
        if (!selectedUser) return;
        setUserEtiquetes(prev => [...prev, { id: etiquetaId }]);
        postAPI('/add_etiqueta', { user: selectedUser, etiqueta: etiquetaId }, () => {});
    };

    const removeEtiqueta = (etiquetaId) => {
        if (!selectedUser) return;
        setUserEtiquetes(prev => prev.filter(e => e.id !== etiquetaId));
        postAPI('/drop_etiqueta', { user: selectedUser, etiqueta: etiquetaId }, () => {});
    };

    // Fetch positions using the optimized batch endpoint
    useEffect(() => {
        if (!selectedUser) {
            setPositionsByEvent([]);
            return;
        }

        if (filteredEvents.length === 0) {
            setPositionsByEvent([]);
            return;
        }

        setLoading(true);

        const eventIds = filteredEvents.map(e => e.id);
        
        postAPI('/stats/casteller-positions', 
            { castellerId: parseInt(selectedUser), eventIds },
            (response) => {
                if (response?.positionsByEvent) {
                    // Map the response to include event objects
                    const eventStatsData = response.positionsByEvent.map(({ eventId, boxes }) => {
                        const event = filteredEvents.find(e => e.id === eventId);
                        return { event, boxes };
                    }).filter(({ event }) => event); // Filter out any events not found
                    
                    setPositionsByEvent(eventStatsData);
                } else {
                    console.error('Invalid response from casteller-positions endpoint');
                    setPositionsByEvent([]);
                }
                setLoading(false);
            }
        );
    }, [selectedUser, filteredEvents]);

    // Calculate prova ranking from user positions - grouped by bundleId + pestanya + cordó (for pinya)
    const provaRanking = useMemo(() => {
        if (!selectedUser || positionsByEvent.length === 0) return [];

        // Filter out hidden events
        const visiblePositionsByEvent = positionsByEvent.filter(({ event }) => !hiddenEventIds.has(event.id));
        const visibleFilteredEvents = filteredEvents.filter(e => !hiddenEventIds.has(e.id));

        const stats = {};

        visiblePositionsByEvent.forEach(({ event, boxes }) => {
            boxes.forEach((box) => {
                const bundleId = box.bundleId;
                if (!bundleId) return;

                // Get the pestanya (part) for this box
                const pestanya = normalizeTabKey(box?.pestanya);
                if (!pestanya) return;

                // For pinya, include cordó in the key
                let cordoGroup = null;
                if (pestanya === 'pinya') {
                    const rawCordo = box?.cordo;
                    const cordoNum = parseInt(rawCordo, 10);
                    if (!isNaN(cordoNum)) {
                        if (cordoNum === 0) cordoGroup = 'nucli';
                        else if (cordoNum === 1) cordoGroup = '1r';
                        else if (cordoNum === 2) cordoGroup = '2n';
                        else if (cordoNum === 3) cordoGroup = '3r';
                        else cordoGroup = '+4';
                    }
                }

                // For tronc, include pis or pom in the key
                let troncGroup = null;
                if (pestanya === 'tronc') {
                    const pom = box?.pom;
                    const pis = box?.pis;
                    if (pom) {
                        // Pom positions (enxaneta, acotxador, dosos)
                        troncGroup = formatPom(pom);
                    } else if (pis !== null && pis !== undefined) {
                        // Floor positions (baix, segon, terç, quart...)
                        troncGroup = formatPis(pis);
                    }
                }

                // Create a unique key combining bundleId, pestanya, and cordó (for pinya) or troncGroup (for tronc)
                let key;
                if (cordoGroup) {
                    key = `${bundleId}__${pestanya}__${cordoGroup}`;
                } else if (troncGroup) {
                    key = `${bundleId}__${pestanya}__${troncGroup}`;
                } else {
                    key = `${bundleId}__${pestanya}`;
                }

                if (!stats[key]) {
                    stats[key] = {
                        bundleId,
                        pestanya,
                        cordoGroup,
                        troncGroup,
                        count: 0,
                        eventCounts: {}, // { eventId: count }
                        boxLabelCounts: {} // Track box labels for pinya with cordó
                    };
                }

                stats[key].count += 1;
                stats[key].eventCounts[event.id] = (stats[key].eventCounts[event.id] || 0) + 1;

                // Track box labels for pinya positions with cordó
                if (pestanya === 'pinya' && cordoGroup) {
                    const { label: boxLabel, hasPerfil } = getBoxLabelInfoFromBox(box);
                    // Only track labels that have a perfil (e.g., "baixos", "crosses", not just "pinya")
                    if (hasPerfil && boxLabel) {
                        stats[key].boxLabelCounts[boxLabel] = (stats[key].boxLabelCounts[boxLabel] || 0) + 1;
                    }
                }
            });
        });

        return Object.entries(stats)
            .map(([key, data]) => {
                const bundle = bundlesById[data.bundleId];
                const label = bundle?.nom ?? data.bundleId;
                // Include visible filtered events, with count 0 for those without this prova+pestanya
                const eventItems = visibleFilteredEvents.map(event => ({
                    event,
                    count: data.eventCounts[event.id] || 0
                }));

                // Find most common box label for pinya with cordó
                let mostCommonBoxLabel = null;
                if (data.pestanya === 'pinya' && data.cordoGroup && Object.keys(data.boxLabelCounts).length > 0) {
                    const sortedLabels = Object.entries(data.boxLabelCounts)
                        .sort((a, b) => b[1] - a[1]);
                    if (sortedLabels.length > 0) {
                        mostCommonBoxLabel = sortedLabels[0][0];
                    }
                }

                return {
                    key,
                    bundleId: data.bundleId,
                    label,
                    pestanya: data.pestanya, // single pestanya for this entry
                    cordoGroup: data.cordoGroup, // cordó group for pinya positions
                    troncGroup: data.troncGroup, // tronc floor/pom group for tronc positions
                    mostCommonBoxLabel, // most common position label for pinya with cordó
                    count: data.count,
                    eventItems
                };
            })
            .sort((a, b) => {
                if (b.count !== a.count) return b.count - a.count;
                if (a.label !== b.label) return a.label.localeCompare(b.label);
                return a.pestanya.localeCompare(b.pestanya);
            });
    }, [selectedUser, positionsByEvent, bundlesById, filteredEvents, hiddenEventIds]);

    // Cordó labels for display
    const CORDO_LABELS = {
        'nucli': 'Nucli',
        '1r': '1r cordó',
        '2n': '2n cordó',
        '3r': '3r cordó',
        '+4': '+4 cordó'
    };

    const CORDO_COLORS = {
        'nucli': '#a78bfa',
        '1r': '#60a5fa',
        '2n': '#34d399',
        '3r': '#fbbf24',
        '+4': '#f87171'
    };

    // Tronc floor/pom labels and colors
    const TRONC_LABELS = {
        'baix': 'Baix del tronc',
        'segon': 'Segon del tronc',
        'terç': 'Terç del tronc',
        'quart': 'Quart del tronc',
        'quint': 'Quint del tronc',
        'sisè': 'Sisè del tronc',
        'setè': 'Setè del tronc',
        'vuitè': 'Vuitè del tronc',
        'novè': 'Novè del tronc',
        'dosos': 'Dosos',
        'acotxador': 'Acotxador',
        'enxaneta': 'Enxaneta'
    };

    const TRONC_COLORS = {
        'baix': '#64748b',
        'segon': '#3b82f6',
        'terç': '#10b981',
        'quart': '#f59e0b',
        'quint': '#ef4444',
        'sisè': '#8b5cf6',
        'setè': '#ec4899',
        'vuitè': '#06b6d4',
        'novè': '#84cc16',
        'dosos': '#f97316',
        'acotxador': '#14b8a6',
        'enxaneta': '#eab308'
    };

    const { eventStats, sortedPositions, chartData, chartOptions, chartEvents, pestanyaTotals, cordoDistribution, positionsByPestanya, unlabeledByPestanya, cordoChartData, cordoChartOptions, pestanyaChartData, pestanyaChartOptions } = useMemo(() => {
        const newPositionCounts = {};
        const positionToPestanya = {};
        const positionHasPerfil = {};
        const cordoCounts = { 'nucli': 0, '1r': 0, '2n': 0, '3r': 0, '+4': 0 };
        const pestanyaCounts = Object.fromEntries(TAB_DEFINITIONS.map(def => [def.key, 0]));

        const stats = positionsByEvent.map(({ event, boxes }) => {
            const eventCounts = Object.fromEntries(TAB_DEFINITIONS.map(def => [def.key, 0]));

            boxes.forEach((box) => {
                // Box now contains: { bundleId, caixaId, pestanya, perfil, cordo }
                const tabKey = getBoxTabFromBox(box);
                if (tabKey && eventCounts[tabKey] !== undefined) {
                    eventCounts[tabKey] += 1;
                    pestanyaCounts[tabKey] += 1;
                }

                // Track cordó distribution for pinya
                if (tabKey === 'pinya') {
                    const rawCordo = box?.cordo;
                    const cordoNum = parseInt(rawCordo, 10);
                    
                    if (!isNaN(cordoNum)) {
                        if (cordoNum === 0) {
                            cordoCounts['nucli'] += 1;
                        } else if (cordoNum === 1) {
                            cordoCounts['1r'] += 1;
                        } else if (cordoNum === 2) {
                            cordoCounts['2n'] += 1;
                        } else if (cordoNum === 3) {
                            cordoCounts['3r'] += 1;
                        } else if (cordoNum > 3) {
                            cordoCounts['+4'] += 1;
                        }
                    }
                }

                const { label, hasPerfil } = getBoxLabelInfoFromBox(box);
                newPositionCounts[label] = (newPositionCounts[label] || 0) + 1;
                positionToPestanya[label] = tabKey || 'altres';
                if (!positionHasPerfil[label]) {
                    positionHasPerfil[label] = hasPerfil;
                }
            });

            const total = Object.values(eventCounts).reduce((sum, value) => sum + value, 0);
            return {
                event,
                counts: eventCounts,
                total
            };
        });

        const sorted = Object.entries(newPositionCounts)
            .sort((a, b) => b[1] - a[1]);

        // Group positions by pestanya (only those with perfil)
        const groupedPositions = {};
        const unlabeledCounts = {};
        const plus4CordoCounts = {}; // Track +4 cordó positions separately for pinya
        TAB_DEFINITIONS.forEach(def => {
            groupedPositions[def.key] = [];
            unlabeledCounts[def.key] = 0;
            plus4CordoCounts[def.key] = 0;
        });
        groupedPositions['altres'] = [];
        unlabeledCounts['altres'] = 0;
        plus4CordoCounts['altres'] = 0;

        sorted.forEach(([label, count]) => {
            const pestanya = positionToPestanya[label] || 'altres';
            if (!groupedPositions[pestanya]) {
                groupedPositions[pestanya] = [];
            }
            // For pinya, group all +4 cordó positions together (with or without perfil)
            if (pestanya === 'pinya' && label.startsWith('+4')) {
                plus4CordoCounts[pestanya] = (plus4CordoCounts[pestanya] || 0) + count;
            } else if (positionHasPerfil[label]) {
                groupedPositions[pestanya].push({ label, count });
            } else {
                unlabeledCounts[pestanya] = (unlabeledCounts[pestanya] || 0) + count;
            }
        });

        // Add +4 cordó as a regular position entry for pinya so it gets sorted normally
        if (plus4CordoCounts['pinya'] > 0) {
            groupedPositions['pinya'].push({ label: 'més de +4 cordó', count: plus4CordoCounts['pinya'] });
            groupedPositions['pinya'].sort((a, b) => b.count - a.count);
        }

        // Pestanya (floor) distribution chart data - filter out zeros except pinya and tronc
        const filteredPestanyes = TAB_DEFINITIONS.filter(def => 
            pestanyaCounts[def.key] > 0 || def.key === 'pinya' || def.key === 'tronc'
        );
        const pestanyaChartData = {
            labels: filteredPestanyes.map(def => def.label),
            datasets: [{
                data: filteredPestanyes.map(def => pestanyaCounts[def.key]),
                backgroundColor: filteredPestanyes.map(def => def.color),
                borderRadius: 4,
                barThickness: 28,
            }]
        };

        const pestanyaChartOptions = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                datalabels: {
                    color: '#374151',
                    anchor: 'end',
                    align: 'right',
                    font: { weight: 'bold', size: 12 },
                    formatter: v => v
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    grid: { display: false },
                    ticks: { display: false },
                    border: { display: false }
                },
                y: {
                    grid: { display: false },
                    ticks: {
                        color: '#374151',
                        font: { size: 13, weight: '500' }
                    },
                    border: { display: false }
                }
            },
            layout: {
                padding: { right: 40 }
            }
        };

        // Cordó distribution chart data
        const cordoLabels = ['Nucli', '1r cordó', '2n cordó', '3r cordó', '+4 cordó'];
        const cordoValues = [cordoCounts['nucli'], cordoCounts['1r'], cordoCounts['2n'], cordoCounts['3r'], cordoCounts['+4']];
        const cordoColors = ['#a78bfa', '#60a5fa', '#34d399', '#fbbf24', '#f87171'];

        const cordoData = {
            labels: cordoLabels,
            datasets: [{
                data: cordoValues,
                backgroundColor: cordoColors,
                borderRadius: 4,
                barThickness: 28,
            }]
        };

        const cordoOptions = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                datalabels: {
                    color: '#374151',
                    anchor: 'end',
                    align: 'right',
                    font: { weight: 'bold', size: 12 },
                    formatter: v => v
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    grid: { display: false },
                    ticks: { display: false },
                    border: { display: false }
                },
                y: {
                    grid: { display: false },
                    ticks: {
                        color: '#374151',
                        font: { size: 13, weight: '500' }
                    },
                    border: { display: false }
                }
            },
            layout: {
                padding: { right: 40 }
            }
        };

        // Chart.js data structure - filter out events with 0 positions and hidden events
        const statsWithPositions = stats.filter(({ total, event }) => total > 0 && !hiddenEventIds.has(event.id));

        const labels = statsWithPositions.map(({ event }) => {
            const date = moment(event['data-esperada-inici']).format('DD/MM');
            if (isMobile) return date;
            const title = event.title?.length > 15 ? event.title.slice(0, 15) + '...' : event.title;
            return `${date} ${title}`;
        });

        const datasets = TAB_DEFINITIONS.map(def => ({
            label: def.label,
            data: statsWithPositions.map(({ counts }) => counts[def.key]),
            backgroundColor: def.color,
            borderRadius: 4,
        }));

        const data = {
            labels,
            datasets
        };

        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: { size: 12 }
                    }
                },
                datalabels: {
                    color: '#fff',
                    font: { weight: 'bold', size: 10 },
                    formatter: v => v > 0 ? v : '',
                    display: (context) => {
                        const value = context.dataset.data[context.dataIndex];
                        if (value <= 0) return false;
                        
                        const chart = context.chart;
                        const chartArea = chart.chartArea;
                        const chartHeight = chartArea.bottom - chartArea.top;
                        const yScale = chart.scales.y;
                        const maxValue = yScale.max;
                        const pixelHeight = (value / maxValue) * chartHeight;
                        
                        return pixelHeight >= 14;
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    stacked: true,
                    grace: '10%',
                    ticks: {
                        stepSize: 1,
                        color: '#718096',
                        font: { size: 12 }
                    },
                    grid: {
                        color: '#e2e8f0',
                        drawBorder: false
                    },
                    border: { display: false }
                },
                x: {
                    stacked: true,
                    grid: {
                        display: false,
                        drawBorder: false
                    },
                    ticks: {
                        color: '#718096',
                        font: { size: 11, weight: '500' },
                        maxRotation: 45,
                        minRotation: 0
                    }
                }
            },
            layout: {
                padding: { top: 20 }
            }
        };

        return {
            eventStats: stats,
            sortedPositions: sorted,
            chartData: data,
            chartOptions: options,
            chartEvents: statsWithPositions,
            pestanyaTotals: pestanyaCounts,
            cordoDistribution: cordoCounts,
            positionsByPestanya: groupedPositions,
            unlabeledByPestanya: unlabeledCounts,
            cordoChartData: cordoData,
            cordoChartOptions: cordoOptions,
            pestanyaChartData,
            pestanyaChartOptions
        };
    }, [positionsByEvent, isMobile, hiddenEventIds]);

    return (
        <div className="stats-positions">
            <StatsDateSelector
                start_date={start_date}
                setStart_date={setStart_date}
                end_date={end_date}
                setEnd_date={setEnd_date}
                minDate={minAllowedDate}
            />

            <div className="stats-positions-filters">
                <div className="stats-positions-filter">
                    <h2>Casteller</h2>
                    <Select
                        options={allUsers}
                        className="basic-select"
                        classNamePrefix="select"
                        isClearable
                        placeholder="Select..."
                        value={allUsers.find(user => `${user.value}` === `${selectedUser}`) || null}
                        onChange={option => setSelectedUser(option?.value || null)}
                    />
                </div>

                <div className="stats-positions-filter">
                    <h2>Actes</h2>
                    <label className="stats-positions-checkbox">
                        <input
                            type="checkbox"
                            checked={includeAssaigs}
                            onChange={() => setIncludeAssaigs(prev => !prev)}
                        />
                        Assaigs
                    </label>
                    <label className="stats-positions-checkbox">
                        <input
                            type="checkbox"
                            checked={includeActuacions}
                            onChange={() => setIncludeActuacions(prev => !prev)}
                        />
                        Actuacions
                    </label>
                </div>
            </div>

            {selectedUser ? (
                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                    {/* Large profile picture with name */}
                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 140 }}>
                        {userProfilePic === null ? (
                            <div style={{
                                width: 120,
                                height: 120,
                                borderRadius: 12,
                                backgroundColor: '#e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <div style={{
                                    width: 28,
                                    height: 28,
                                    border: '3px solid #ccc',
                                    borderTopColor: '#888',
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                }} />
                            </div>
                        ) : (
                            <img 
                                src={userProfilePic || PLACEHOLDER_PROFILE_NO_PHOTO}
                                alt=""
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 12,
                                    objectFit: 'cover',
                                    backgroundColor: '#e0e0e0',
                                }}
                            />
                        )}
                        {/* Name below photo */}
                        <div style={{ marginTop: 10, textAlign: 'center' }}>
                            <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1f2937' }}>
                                {castellersInfo?.[selectedUser]?.mote || ''}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                {getCastellerFullName(castellersInfo?.[selectedUser], '')}
                            </div>
                        </div>
                    </div>
                    {/* Etiquetes section */}
                    <div className="stats-positions-section" style={{ flex: 1, marginTop: 0 }}>
                        <h2>Etiquetes del casteller</h2>
                        {loadingEtiquetes || loadingUserEtiquetes ? (
                            <div className="stats-positions-etiquetes-loading">
                                <span className="stats-positions-spinner"></span>
                                Carregant etiquetes...
                            </div>
                        ) : (
                            <div className="stats-positions-etiquetes">
                                {allEtiquetes.map(etiqueta => {
                                    const hasEtiqueta = userEtiquetes.some(e => e.id === etiqueta.id);
                                    return (
                                        <button
                                            key={etiqueta.id}
                                            className={`stats-positions-etiqueta ${hasEtiqueta ? 'active' : ''}`}
                                            onClick={() => hasEtiqueta ? removeEtiqueta(etiqueta.id) : addEtiqueta(etiqueta.id)}
                                            style={{
                                                backgroundColor: hasEtiqueta ? '#dbeafe' : '#ffffff',
                                                color: hasEtiqueta ? '#1d4ed8' : '#374151',
                                                borderColor: hasEtiqueta ? '#3b82f6' : '#d1d5db'
                                            }}
                                        >
                                            <span className="stats-positions-etiqueta-icon">{hasEtiqueta ? '−' : '+'}</span>
                                            {etiqueta.nom}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            <div className="stats-positions-section">
                <h2>Resum de proves per pis d'un casteller</h2>
                {!selectedUser && <p>Selecciona un casteller per veure les seves posicions.</p>}
                {selectedUser && loading && <p>Carregant dades de posicions...</p>}
                {selectedUser && !loading && eventStats.length === 0 && (
                    <p>No hi ha dades de posicions per aquest període.</p>
                )}

                {hiddenEventIds.size > 0 ? (
                    <p style={{ fontSize: '0.9em', color: '#6b7280', marginBottom: 10 }}>
                        {hiddenEventIds.size} {hiddenEventIds.size === 1 ? 'esdeveniment ocult' : 'esdeveniments ocults'}.{' '}
                        <button 
                            onClick={() => setHiddenEventIds(new Set())}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                color: '#3b82f6', 
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: 0,
                                font: 'inherit'
                            }}
                        >
                            Mostra'ls tots
                        </button>
                    </p>
                ) : null}

                {selectedUser && !loading && eventStats.length > 0 && (
                    <div className="stats-positions-chart-container">
                        <Bar 
                            data={chartData} 
                            options={{
                                ...chartOptions,
                                onClick: (_, elements) => {
                                    if (elements.length > 0) {
                                        const eventId = chartEvents[elements[0].index]?.event?.id;
                                        if (eventId) {
                                            setHiddenEventIds(prev => new Set([...prev, eventId]));
                                        }
                                    }
                                }
                            }} 
                        />
                    </div>
                )}
            </div>

            {selectedUser && !loading && sortedPositions.length > 0 ? (
                <>
                    <div className="stats-positions-distribution-row">
                        <div className="stats-positions-section stats-positions-distribution-item">
                            <h2>Distribució de pisos</h2>
                            <div className="stats-positions-cordo-chart" style={{ height: pestanyaChartData.labels.length * 45 }}>
                                <Bar data={pestanyaChartData} options={pestanyaChartOptions} />
                            </div>
                        </div>

                        {pestanyaTotals.pinya > 0 ? (
                            <div className="stats-positions-section stats-positions-distribution-item">
                                <h2>Distribució de cordons a la pinya</h2>
                                <div className="stats-positions-cordo-chart" style={{ height: cordoChartData.labels.length * 45 }}>
                                    <Bar data={cordoChartData} options={cordoChartOptions} />
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Proves ranking section */}
                    {provaRanking.length > 0 ? (
                        <div className="stats-positions-section">
                            <h2>Proves més habituals del casteller</h2>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 16,
                                marginTop: 16
                            }}>
                                {provaRanking.map((prova, index) => {
                                    const { weeks, activeDays } = groupByWeek(prova.eventItems);
                                    // Calculate card width based on grid: day labels (20px) + weeks * (16px + 2px gap) + padding (32px)
                                    const gridWidth = 20 + weeks.length * 18 + 32;
                                    return (
                                        <div
                                            key={prova.key}
                                            style={{
                                                background: '#fff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: 12,
                                                padding: 16,
                                                minWidth: Math.max(gridWidth, 200),
                                                maxWidth: Math.max(gridWidth, 200),
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                gap: 12
                                            }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={{
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            color: '#94a3b8',
                                                            flexShrink: 0
                                                        }}>
                                                            #{index + 1}
                                                        </span>
                                                        <span style={{
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                            color: '#1e293b',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        title={prova.label}
                                                        >
                                                            {prova.label}
                                                        </span>
                                                    </div>
                                                    {/* Pills for pestanya, cordó or most common box label */}
                                                    <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                                                        {/* Show pestanya pill only for folre, manilles, puntals, organitzacio (not pinya/tronc which have more specific pills) */}
                                                        {prova.pestanya && !['pinya', 'tronc'].includes(prova.pestanya) ? (
                                                            <span
                                                                style={{
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    color: '#fff',
                                                                    background: PART_COLORS[prova.pestanya] || '#64748b',
                                                                    padding: '2px 5px',
                                                                    borderRadius: 4,
                                                                    textTransform: 'capitalize'
                                                                }}
                                                            >
                                                                {prova.pestanya}
                                                            </span>
                                                        ) : null}
                                                        {/* Show box label if available, otherwise show cordó for pinya */}
                                                        {prova.mostCommonBoxLabel ? (
                                                            <span
                                                                style={{
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    color: '#fff',
                                                                    background: CORDO_COLORS[prova.cordoGroup] || '#64748b',
                                                                    padding: '2px 5px',
                                                                    borderRadius: 4
                                                                }}
                                                            >
                                                                {prova.mostCommonBoxLabel}
                                                            </span>
                                                        ) : prova.cordoGroup ? (
                                                            <span
                                                                style={{
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    color: '#fff',
                                                                    background: CORDO_COLORS[prova.cordoGroup] || '#64748b',
                                                                    padding: '2px 5px',
                                                                    borderRadius: 4
                                                                }}
                                                            >
                                                                {CORDO_LABELS[prova.cordoGroup] || prova.cordoGroup}
                                                            </span>
                                                        ) : null}
                                                        {/* Show tronc floor/pom pill */}
                                                        {prova.troncGroup ? (
                                                            <span
                                                                style={{
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    color: '#fff',
                                                                    background: TRONC_COLORS[prova.troncGroup] || '#64748b',
                                                                    padding: '2px 5px',
                                                                    borderRadius: 4
                                                                }}
                                                            >
                                                                {TRONC_LABELS[prova.troncGroup] || prova.troncGroup}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: '#475569',
                                                    background: '#f1f5f9',
                                                    padding: '3px 8px',
                                                    borderRadius: 999,
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0
                                                }}>
                                                    {prova.count} cops
                                                </span>
                                            </div>
                                            <div style={{ marginTop: 12 }}>
                                                <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                                                    {/* Month labels row */}
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <div style={{ width: 16, marginRight: 4 }} />
                                                        {weeks.map((week, weekIdx) => {
                                                            const month = week.weekStart.month();
                                                            const prevMonth = weekIdx > 0 ? weeks[weekIdx - 1].weekStart.month() : -1;
                                                            const showMonth = month !== prevMonth;
                                                            return (
                                                                <div 
                                                                    key={weekIdx}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 12,
                                                                        fontSize: 7,
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'flex-end',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    {showMonth ? MONTHS[month] : ''}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Grid */}
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        {/* Day labels */}
                                                        <div style={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            gap: 2,
                                                            marginRight: 4
                                                        }}>
                                                            {activeDays.map(dayIdx => (
                                                                <div 
                                                                    key={dayIdx}
                                                                    style={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        fontSize: 8,
                                                                        color: '#94a3b8',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-end'
                                                                    }}
                                                                >
                                                                    {DAYS[dayIdx]}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {/* Weeks */}
                                                        {weeks.map((week, weekIdx) => (
                                                            <div 
                                                                key={weekIdx}
                                                                style={{ 
                                                                    display: 'flex', 
                                                                    flexDirection: 'column', 
                                                                    gap: 2 
                                                                }}
                                                            >
                                                                {activeDays.map(dayIdx => {
                                                                    const day = week.days[dayIdx];
                                                                    const hasData = day !== null;
                                                                    const count = day?.count || 0;
                                                                    const isActuacio = day?.events?.some(e => e.tipus === 'actuació');
                                                                    const tooltip = day 
                                                                        ? `${day.events.map(e => `${moment(e['data-esperada-inici']).format('DD/MM')} · ${e.title}`).join('\n')} - ${count} ${count === 1 ? 'posició' : 'posicions'}\n(clic per amagar)`
                                                                        : '';
                                                                    
                                                                    const handleClick = () => {
                                                                        if (day?.events) {
                                                                            const eventIds = day.events.map(e => e.id);
                                                                            setHiddenEventIds(prev => new Set([...prev, ...eventIds]));
                                                                        }
                                                                    };

                                                                    const bgColor = !hasData 
                                                                        ? 'transparent' 
                                                                        : count > 0 
                                                                            ? (isActuacio ? '#f59e0b' : '#3b82f6')
                                                                            : (isActuacio ? '#fef3c7' : '#e2e8f0');
                                                                    
                                                                    return (
                                                                        <div
                                                                            key={dayIdx}
                                                                            title={tooltip}
                                                                            onClick={hasData ? handleClick : undefined}
                                                                            style={{
                                                                                width: 16,
                                                                                height: 16,
                                                                                borderRadius: 2,
                                                                                background: bgColor,
                                                                                border: 'none',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                fontSize: 8,
                                                                                fontWeight: 600,
                                                                                color: count > 0 ? '#fff' : '#94a3b8',
                                                                                cursor: hasData ? 'pointer' : 'default'
                                                                            }}
                                                                        >
                                                                            {count > 1 ? count : ''}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div className="stats-positions-section">
                        <h2>Posicions més habituals</h2>
                        <div className="stats-positions-groups">
                            {TAB_DEFINITIONS
                                .map(def => ({
                                    ...def,
                                    positions: positionsByPestanya[def.key] || [],
                                    unlabeled: unlabeledByPestanya[def.key] || 0,
                                    total: (positionsByPestanya[def.key] || []).reduce((sum, p) => sum + p.count, 0) + (unlabeledByPestanya[def.key] || 0)
                                }))
                                .filter(group => group.positions.length > 0 || group.unlabeled > 0)
                                .sort((a, b) => b.total - a.total)
                                .map(({ key, label, color, positions, unlabeled, total }) => {
                                    const isCollapsed = collapsedGroups[key];

                                    return (
                                        <div key={key} className="stats-positions-group">
                                            <button
                                                className="stats-positions-group-header"
                                                onClick={() => toggleGroup(key)}
                                                style={{ borderLeftColor: color }}
                                            >
                                                <span className="stats-positions-group-arrow">
                                                    {isCollapsed ? '▶' : '▼'}
                                                </span>
                                                <span className="stats-positions-group-title">{label}</span>
                                                <span className="stats-positions-group-total">{total}</span>
                                            </button>
                                            {!isCollapsed ? (
                                                <ul className="stats-positions-list">
                                                    {positions.map(({ label, count }) => (
                                                        <li key={label}>
                                                            <strong>{count}</strong> cops {getPreposition(label)}{label}
                                                        </li>
                                                    ))}
                                                    {unlabeled > 0 ? (
                                                        <li className="stats-positions-unlabeled">
                                                            {unlabeled} a posicions sense etiquetar
                                                        </li>
                                                    ) : null}
                                                </ul>
                                            ) : null}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </>
            ) : null}

            {selectedUser && !loading && sortedPositions.length === 0 ? (
                <div className="stats-positions-section">
                    <h2>Posicions més habituals</h2>
                    <p>No hi ha dades de posicions per aquest període.</p>
                </div>
            ) : null}

            {!selectedUser ? (
                <StatsComparativaPosicions
                    castellersInfo={castellersInfo}
                    start_date={start_date}
                    end_date={end_date}
                    includeAssaigs={includeAssaigs}
                    includeActuacions={includeActuacions}
                    onSelectUser={setSelectedUser}
                />
            ) : null}
        </div>
    );
}

export default StatsPositionsTab;
